import { Injectable, inject } from '@angular/core';
import { Asset, bookingDurationHours } from '../models/asset.model';
import { AssetService } from './asset.service';
import { Booking } from '../models/booking.model';
import {
  onlineFeeEurosFromBooking,
  ownerBalanceEurosFromBooking,
  rentalCentsFromBooking,
} from '../shared/booking/booking-amounts.utils';

export interface ContractRenderContext {
  totalPrice: number;
  date: string;
  startTime: string;
  endTime: string;
  /** Whether the user opted into the optional cleaning add-on. */
  includesCleaning?: boolean;
  /**
   * Legal name of the owner for the REUNIDOS section. Caller should pass the
   * SEPA account-holder snapshot from the booking (`ownerAccountHolder`) when
   * available, since that is the most legally accurate identifier; otherwise
   * the service falls back to `space.ownerDisplayName` and finally to a
   * neutral "el ARRENDADOR" string.
   */
  ownerName?: string;
  /** Checkout snapshots when rendering a signed booking contract. */
  bookingSnapshots?: Pick<
    Booking,
    | 'bookingPriceCents'
    | 'applicationFeeCents'
    | 'commissionAmountCents'
    | 'commissionRateApplied'
    | 'payoutModeAtCheckout'
    | 'payment_status'
  >;
}

const ANNEX_HEADING_REGEX =
  /<h[1-4][^>]*>\s*(?:<strong>)?\s*ANEXO:\s*NORMAS\s+PARTICULARES\s*(?:<\/strong>)?\s*<\/h[1-4]>/i;

const ANNEX_HEADER_HTML =
  '<h3 style="text-align: center; margin: 50px 0;"><strong>ANEXO: NORMAS PARTICULARES</strong></h3>';

/**
 * Builds the unified lease contract (platform base terms + space annex)
 * shown at checkout and snapshotted into the booking record for audit.
 */
@Injectable({ providedIn: 'root' })
export class BookingContractService {
  private assetService = inject(AssetService);

  async composeUnifiedContractHtml(space: Asset, ctx: ContractRenderContext): Promise<string> {
    const baseTemplate = await this.assetService.ensureDefaultContractTemplate();
    const stored = (space.contractTemplateHtml ?? '').trim();
    const vars = this.#buildVariables(space, ctx);

    let rawHtml: string;
    if (stored && this.#looksLikeFullContract(stored, baseTemplate)) {
      rawHtml = stored;
    } else {
      const annex = stored ? this.#extractCustomAnnex(stored, baseTemplate) : '';
      rawHtml = annex
        ? `${baseTemplate}\n${ANNEX_HEADER_HTML}\n${annex}`
        : `${baseTemplate}\n${ANNEX_HEADER_HTML}\n<p>Sin normas particulares adicionales.</p>`;
    }

    return this.#interpolate(rawHtml, vars);
  }

  #looksLikeFullContract(stored: string, baseTemplate: string): boolean {
    if (ANNEX_HEADING_REGEX.test(stored)) return true;
    const base = baseTemplate.trim();
    return !!base && stored.startsWith(base);
  }

  #extractCustomAnnex(storedTemplateHtml: string, baseTemplate: string): string {
    const raw = storedTemplateHtml.trim();
    const annexMatch = ANNEX_HEADING_REGEX.exec(raw);
    if (annexMatch && annexMatch.index >= 0) {
      return raw.slice(annexMatch.index + annexMatch[0].length).trim();
    }

    const baseHtml = baseTemplate.trim();
    if (baseHtml && raw.startsWith(baseHtml)) {
      return raw.slice(baseHtml.length).replace(ANNEX_HEADING_REGEX, '').trim();
    }

    return raw;
  }

  #buildVariables(space: Asset, ctx: ContractRenderContext): Record<string, string> {
    const bookingLike = {
      totalPrice: ctx.totalPrice,
      ...ctx.bookingSnapshots,
    };
    const totalPrice = this.#round2(rentalCentsFromBooking(bookingLike) / 100);
    const platformFee = this.#round2(onlineFeeEurosFromBooking(bookingLike as Booking));
    const pendingBalance = this.#round2(ownerBalanceEurosFromBooking(bookingLike as Booking));
    const cleaningPenalty = this.#round2(space.pricing?.cleaningFee ?? 0);
    const hours = bookingDurationHours(ctx.startTime, ctx.endTime);

    // Legal age default: 18 (legal majority in Spain) when the space hasn't
    // set a more restrictive minimum. Some venues (e.g. nightlife) may want
    // 21 or higher — stored in `conditions.minAge`.
    const minAge = Math.max(18, space.conditions?.minAge ?? 18);

    // Cancellation notice in days (space-configurable). 0 means "no free
    // cancellation policy" — handled in the template wording itself.
    const cancellationDays = Math.max(0, space.conditions?.cancellationDays ?? 0);

    const ownerName = (ctx.ownerName?.trim())
      || (space.ownerDisplayName?.trim())
      || 'el ARRENDADOR';

    return {
      spaceName: space.name ?? 'Espacio',
      totalPrice: String(totalPrice),
      platformFee: String(platformFee),
      depositAmount: String(pendingBalance),
      pendingBalance: String(pendingBalance),
      securityDeposit: String(space.securityDeposit ?? 0),
      cleaningPenalty: String(cleaningPenalty),
      maxCapacity: String(space.capacity ?? 0),
      minAge: String(minAge),
      cancellationDays: String(cancellationDays),
      ownerName,
      date: ctx.date,
      startTime: ctx.startTime,
      endTime: ctx.endTime,
      durationHours: String(hours),
    };
  }

  #interpolate(html: string, vars: Record<string, string>): string {
    return html.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => vars[key] ?? '');
  }

  #round2(n: number): number {
    return Math.round(n * 100) / 100;
  }
}
